import { useState } from "react";
import {
  Check,
  Play,
  MessageCircle,
  Instagram,
  Phone,
  ArrowLeft,
  Store,
  Tag,
  Wrench,
  ListChecks,
  X,
} from "lucide-react";
import type { DemoFormData } from "../demoTypes";
import type { DemoFlow } from "../flowTypes";
import { summarizeFlow, describeFlowSteps, type FlowStepDetail } from "../flowBuilder";

export default function DemoPreview({
  data,
  flow,
  onStart,
  onReset,
}: {
  data: DemoFormData;
  flow: DemoFlow;
  onStart: () => void;
  onReset: () => void;
}) {
  const [showFlow, setShowFlow] = useState(false);
  const previewImages = data.gorseller.slice(0, 3);
  const summary = summarizeFlow(flow);
  const flowDetails = describeFlowSteps(flow);

  const kanalIcon =
    data.anaIletisimKanali === "instagram" ? (
      <Instagram className="h-4 w-4" />
    ) : data.anaIletisimKanali === "telefon" ? (
      <Phone className="h-4 w-4" />
    ) : (
      <MessageCircle className="h-4 w-4" />
    );

  const rows = [
    { icon: <Store className="h-4 w-4" />, label: "Firma", value: data.firmaAdi || "—" },
    { icon: <Tag className="h-4 w-4" />, label: "Sektör", value: data.sektor || "—" },
    {
      icon: <Wrench className="h-4 w-4" />,
      label: "Ana hizmet",
      value: data.anaHizmet || data.hizmetler[0] || "—",
    },
    { icon: kanalIcon, label: "İletişim", value: data.whatsapp || "—" },
  ];

  return (
    <div className="min-h-screen bg-background-100 px-4 py-10 md:py-16">
      <div className="animate-fade-in-up mx-auto max-w-2xl">
        {/* Başlık */}
        <div className="text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-500 text-primary-950">
            <Check className="h-7 w-7" strokeWidth={2.5} />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            Demo Deneyiminiz Hazır
          </h1>
          <p className="mt-2 text-sm text-foreground-500 md:text-base">
            İşletmenize özel deneyimin oluşturulması tamamlandı.
          </p>
        </div>

        {/* Özet kartı */}
        <div className="mt-8 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          {(data.logo || previewImages.length > 0) && (
            <div className="flex items-center gap-3 border-b border-background-200 bg-background-100/50 p-4">
              {data.logo && (
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-background-200 bg-background-50">
                  <img src={data.logo} alt="Logo" className="h-full w-full object-cover object-top" />
                </div>
              )}
              {previewImages.map((img) => (
                <div
                  key={img}
                  className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-background-200 bg-background-50"
                >
                  <img src={img} alt="Görsel" className="h-full w-full object-cover object-top" />
                </div>
              ))}
            </div>
          )}

          <div className="divide-y divide-background-100">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center gap-3 px-5 py-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  {row.icon}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground-400">{row.label}</p>
                  <p className="truncate text-sm font-semibold text-foreground-900">{row.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Akış özeti */}
        <div className="mt-8 rounded-2xl border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
              <ListChecks className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-heading text-sm font-bold text-foreground-950 md:text-base">
                Akışınız oluşturuldu
              </h2>
              <p className="text-xs text-foreground-500 md:text-sm">{summary.title}</p>
            </div>
          </div>
          <ul className="mt-4 space-y-2">
            {summary.bullets.map((b) => (
              <li key={b} className="flex items-center gap-2.5 text-sm text-foreground-700">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                {b}
              </li>
            ))}
          </ul>
          <button
            onClick={() => setShowFlow(true)}
            className="mt-5 flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100"
          >
            <ListChecks className="h-4 w-4" />
            Akışı Gör
          </button>
        </div>

        {/* CTA */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            onClick={onStart}
            className="group flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-background-50 transition hover:bg-primary-600 sm:w-auto sm:min-w-[240px]"
          >
            <Play className="h-5 w-5 transition-transform group-hover:scale-110" />
            Demoyu Başlat
          </button>
          <button
            onClick={onReset}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-foreground-500 transition hover:text-foreground-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Başa Dön
          </button>
        </div>
      </div>

      {showFlow && <FlowModal details={flowDetails} onClose={() => setShowFlow(false)} />}
    </div>
  );
}

function FlowModal({
  details,
  onClose,
}: {
  details: FlowStepDetail[];
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="animate-fade-in-up max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-background-200 bg-background-50 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-foreground-950">Akış Adımları</h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition hover:bg-background-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ol className="space-y-3">
          {details.map((d, i) => (
            <li key={i} className="rounded-xl border border-background-200 bg-background-50 p-3">
              <div className="flex items-center gap-2.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-bold text-primary-700">
                  {i + 1}
                </span>
                <span className="text-sm font-semibold text-foreground-900">{d.label}</span>
              </div>
              <p className="mt-1.5 pl-8 text-sm text-foreground-500">{d.content}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}